import { useState } from "react";

import useDialogStore from "@store/useDialogStore";
import { useUserpilot } from "@platform/hooks/useUserpilot";
import { useEscapeKey } from "@hooks/useEscapeKey";
import { Source } from "@enums/common.enum";
import { ICompany } from "@interfaces/company.interface";
import { CreateCompanyRequest } from "@app-types/requests.type";
import { BaseDialog } from "@ui/Dialogs/BaseDialog";
import { CompanyForm } from "./CompanyForm";

interface Props {
  query: string;
  onSave: (data: CreateCompanyRequest) => Promise<ICompany>;
  sourceItem: string;
  sourcePlatform: Source;
}

export function AddNewCompanyDialog({ query, onSave, sourceItem, sourcePlatform }: Props) {
  const { track } = useUserpilot();
  const { isOpen, closeDialog } = useDialogStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async (data: CreateCompanyRequest) => {
    setIsSubmitting(true);

    try {
      await onSave(data);
      track(`Click: Create company submit (${sourceItem})`);
      return true;
    } catch {
      // useCreateCompany shows a toaster message so we just suppress the error here
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => closeDialog();
  useEscapeKey({ callback: closeDialog, enabled: isOpen, ignoreWhenFocusedOnInput: true });

  return (
    <BaseDialog
      key={query}
      isFullScreen
      title="New Company"
      bodyClassName="min-h-[calc(100vh_-_62px)]"
    >
      <CompanyForm
        initialTitle={query}
        sourcePlatform={sourcePlatform}
        onCancel={handleDialogClose}
        onSubmit={handleSave}
        isSubmitting={isSubmitting}
        showAttributes
      />
    </BaseDialog>
  );
}
